import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getMediaPage, type GalleryFilter } from '../api/confetti'
import { FilterTabs, type GalleryTab } from '../components/FilterTabs'
import { MediaGrid } from '../components/MediaGrid'
import { UploadNotice } from '../components/UploadNotice'
import { Spinner } from '../components/icons'
import { useSession } from '../session/context'
import { useAlbum } from '../layouts/AlbumContext'

/** Vertaalt een tab naar de filters die de API kent. */
function filterFor(tab: GalleryTab, guestId: string): GalleryFilter {
  if (tab === 'mijn') {
    return { guestId }
  }

  if (tab === 'videos') {
    return { kind: 'Video' }
  }

  return {}
}

function tabFrom(raw: string | null): GalleryTab {
  return raw === 'mijn' || raw === 'videos' ? raw : 'alles'
}

function tabFromWithoutMine(raw: string | null): GalleryTab {
  return raw === 'videos' ? raw : 'alles'
}

/** Ronde 6 en 7 — het album. */
export function GalleryPage() {
  const { slug, guest } = useSession()
  const { queue, openPanel, profile, readOnly } = useAlbum()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  // Zonder gast bestaat "mijn foto's" niet, en dan kan de tab ook niet gekozen
  // worden — een `?tab=mijn` in de adresbalk valt terug op alles.
  const tab = readOnly ? tabFromWithoutMine(params.get('tab')) : tabFrom(params.get('tab'))
  const guestId = guest?.guestId ?? ''
  const filter = useMemo(() => filterFor(tab, guestId), [tab, guestId])

  const media = useInfiniteQuery({
    queryKey: ['media', slug, tab, guestId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => getMediaPage(slug, filter, pageParam, signal),
    getNextPageParam: (page) => page.nextCursor,
  })

  const items = useMemo(() => media.data?.pages.flatMap((page) => page.items) ?? [], [media.data])

  // De items die nog onderweg zijn horen vooraan het grid, maar alleen in de
  // tabs waar ze straks ook echt terechtkomen.
  const pending = queue.items.filter((item) => {
    if (item.status === 'done' || item.status === 'cancelled') {
      return false
    }

    return tab === 'videos' ? item.kind === 'Video' : true
  })

  const sentinel = useRef<HTMLDivElement>(null)

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = media

  // Onderaan het grid staat een leeg blokje; komt dat in beeld, dan is er een
  // pagina bij. De afhankelijkheden zijn de drie losse waarden en niet het hele
  // query-object: dat krijgt bij elke render een nieuwe identiteit, en dan zou
  // de waarnemer per render afgebroken en opnieuw opgezet worden.
  useEffect(() => {
    const element = sentinel.current

    if (!element || !hasNextPage) {
      return
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) {
        void fetchNextPage()
      }
    })

    observer.observe(element)

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  function selectTab(next: GalleryTab) {
    setParams(
      (current) => {
        const params = new URLSearchParams(current)

        if (next === 'alles') {
          params.delete('tab')
        } else {
          params.set('tab', next)
        }

        return params
      },
      { replace: true },
    )
  }

  return (
    <div className="relative flex flex-col lg:min-h-0 lg:flex-1">
      {/* De actieknop staat niet in deze pagina: op desktop in AlbumLayout,
          op mobiel in de onderbalk. Zou hij hier staan, dan zat hij in de
          galerij naast de tabs en in challenges naast een titel — twee plekken
          die net niet gelijk liggen, dus verspringt hij bij het wisselen. De
          ruimte rechts is voor de knop op desktop. */}
      <div className="flex shrink-0 items-center gap-6 px-5 pt-2 lg:px-9 lg:pt-6.5 lg:pr-64 lg:pb-5">
        <FilterTabs
          active={tab}
          mineCount={profile?.uploadCount ?? null}
          showMine={!readOnly}
          onChange={selectTab}
        />
      </div>

      {/* De banner hoort bij werk dat nog loopt. Blijft hij staan als alles
          klaar is, dan is het een mededeling die niemand meer nodig heeft —
          wat er geüpload is staat in het grid eronder. */}
      {(queue.stats.busy || queue.stats.failed > 0) && (
        <div className="shrink-0 px-5 pt-4 lg:px-9 lg:pt-0 lg:pb-5">
          <UploadNotice stats={queue.stats} onClick={() => openPanel('uploads')} />
        </div>
      )}

      <div className="px-5 pt-3 pb-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:px-9 lg:pt-0 lg:pb-9">
        {media.isPending ? (
          <div className="flex justify-center py-16">
            <Spinner className="text-accent size-6" />
          </div>
        ) : items.length === 0 && pending.length === 0 ? (
          <EmptyState tab={tab} onAdd={readOnly ? undefined : () => openPanel('toevoegen')} />
        ) : (
          <>
            <MediaGrid
              items={items}
              pending={pending}
              // Alleen als alles door elkaar staat. Zie MediaGrid.
              showNames={tab === 'alles'}
              // De tab gaat mee, want het detail telt "142 van 312" binnen de
              // lijst waaruit je het opende.
              onOpen={(id) =>
                navigate({ pathname: id, search: tab === 'alles' ? '' : `?tab=${tab}` })
              }
            />

            <div ref={sentinel} className="h-8" />

            {media.isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Spinner className="text-accent size-5" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function EmptyState({ tab, onAdd }: { tab: GalleryTab; onAdd?: () => void }) {
  const copy = {
    alles: {
      title: 'Nog niets in het album',
      detail: 'Jij mag de eerste zijn. Alles wat je deelt komt hier meteen te staan.',
    },
    mijn: {
      title: 'Je hebt nog niets gedeeld',
      detail: 'Maak een foto of kies er een uit je toestel.',
    },
    videos: {
      title: 'Nog geen video’s',
      detail: 'Film iets korts — de openingsdans doet het altijd goed.',
    },
  }[tab]

  return (
    <div className="flex flex-col items-center px-4 py-16 text-center">
      <h2 className="font-display text-[30px] leading-tight">{copy.title}</h2>
      <p className="text-ink-soft mt-2.5 max-w-[320px] text-sm leading-relaxed text-pretty">
        {copy.detail}
      </p>

      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="border-line text-ink-muted mt-6 cursor-pointer rounded-full border px-5 py-2.5 text-sm"
        >
          Foto’s toevoegen
        </button>
      )}
    </div>
  )
}
